import { createElement, memo, useState, useEffect, useCallback } from '@max/max';
import View from '@hfe/max-view';
import Text from '@hfe/max-text';
import Image from '@hfe/max-image';
import Sensor from '@max/mbc-sensor';
import { useQueryActivityInfo, useDrawPrize } from '@/apis';
import { useIntentionData } from '@/hooks/useIntentionData';
import { PrizeStatus, PrizeType } from '@/types/shared';
import { CouponCardSingle, CouponCardDouble, CouponCardTriple, CouponHorizontal } from './components/cards';
import { processPrizeList } from '@/utils/prizeProcess';
import './index.scss';
import { LayoutType } from './types/shared';
import { useMonitorEngineContext, useMonitorNavigation } from '@nibfe/mcp-monitor-sdk';
import { getPlatformType, PlatformType } from '@nibfe/mkt-env-api';
import { LX_CONFIG } from '@/constants';
const CrowdCouponComponent = (props) => {
    const { activityId, content, layoutType = LayoutType.SINGLE, packageBackgroundImg, packageBackgroundWidth = 702, packageBackgroundHeight = 356, packageMarginTop = 0, isShowClaimAll, claimOnClickConfig, title, titleColor, isLinkedTab, tabBusinessLines = [] } = props;
    const [prizeList, setPrizeList] = useState([]);
    const { queryActivityInfo } = useQueryActivityInfo();
    const { drawPrize } = useDrawPrize();
    const ctx = useMonitorEngineContext();
    const { monitorNavigateTo } = useMonitorNavigation();
    const { queryIntentionInfo, maxIntentionBiz } = useIntentionData();
    // 加载活动信息
    useEffect(() => {
        if (!activityId) {
            return;
        }
        const loadActivityInfo = async () => {
            let intentionInfo = null;
            if (isLinkedTab) {
                intentionInfo = await queryIntentionInfo({
                    activityId
                }).catch(() => { });
                if (maxIntentionBiz.current) {
                    ctx._communication?.emit('TabFollowEvent', {
                        index: tabBusinessLines?.findIndex(line => line.bizType === maxIntentionBiz.current)
                    });
                }
            }
            const result = await queryActivityInfo({
                activityId,
                extra: {
                    bizType2IntentLevelMap: intentionInfo ?? {}
                }
            });
            if (result?.moduleInfoList?.[0]?.rewardModuleInfoVO?.userPrizeInfos) {
                const rawPrizeList = result.moduleInfoList[0].rewardModuleInfoVO.userPrizeInfos;
                const processedPrizeList = processPrizeList(rawPrizeList);
                setPrizeList(processedPrizeList);
            }
        };
        loadActivityInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId]);
    // 根据返回的券数据更新 prizeList
    const updatePrizeListWithReturnData = (returnPrizes) => {
        const processedReturnPrizes = processPrizeList(returnPrizes);
        setPrizeList((prevList) => {
            return prevList.map((item) => {
                const updatedPrize = processedReturnPrizes.find(p => p.prizeBatchId === item.prizeBatchId);
                if (updatedPrize) {
                    // 只合并 updatedPrize 中存在值的字段，其他字段保持原值
                    const newRes = {
                        ...item
                    };
                    Object.keys(updatedPrize).forEach(key => {
                        const value = updatedPrize[key];
                        if (value !== null && value !== undefined) {
                            // @ts-ignore
                            newRes[key] = value;
                        }
                    });
                    return newRes;
                }
                return item;
            });
        });
    };
    // 领取奖励
    const handleDrawPrize = useCallback(async (prize) => {
        // 上报领取点击埋点
        ctx._LXReport.mcReport(LX_CONFIG.COUPON_CARD_MC, {
            valLab: {
                coupon_batch_id: prize.prizeBatchId,
                coupon_status: prize.prizeStatus
            }
        });
        if (prize.prizeStatus === PrizeStatus.NOT_USED || prize.prizeStatus === PrizeStatus.DRAWN) {
            // 去使用
            let url = prize.jumpUrl;
            if (!url) {
                if (!prize.rewardType) {
                    ctx._uiInteractionAPI.toast.show({
                        content: '跳转失败'
                    });
                    return;
                }
                const mapper = {
                    [PrizeType.NEXT_COUPON]: {
                        [PlatformType.mtMinaWebview]: '/hotel/pages/list/index',
                        [PlatformType.mtHotelMinaWebview]: '/pages/list/index',
                        [PlatformType.mtApp]: 'imeituan://www.meituan.com/hotel/list',
                        [PlatformType.mtAppWebView]: 'imeituan://www.meituan.com/hotel/list'
                    },
                    [PrizeType.GOD_COUPON]: {
                        [PlatformType.mtMinaWebview]: '/hotel/pages/list/index',
                        [PlatformType.mtHotelMinaWebview]: '/pages/list/index',
                        [PlatformType.mtApp]: 'imeituan://www.meituan.com/hotel/list',
                        [PlatformType.mtAppWebView]: 'imeituan://www.meituan.com/hotel/list'
                    },
                    [PrizeType.TOP_COUPON]: {
                        [PlatformType.mtMinaWebview]: '/hotel/pages/list/index',
                        [PlatformType.mtHotelMinaWebview]: '/pages/list/index',
                        [PlatformType.mtApp]: 'imeituan://www.meituan.com/hotel/list',
                        [PlatformType.mtAppWebView]: 'imeituan://www.meituan.com/hotel/list'
                    },
                    [PrizeType.POINTS]: {
                        [PlatformType.mtMinaWebview]: '/hotel/pages/list/index?preCheckedFast={"service_info_poi":"803343"}',
                        [PlatformType.mtHotelMinaWebview]: '/pages/list/index?preCheckedFast={"service_info_poi":"803343"}',
                        [PlatformType.mtApp]: 'imeituan://www.meituan.com/hotel/list?_page_new=1&dsp_init_mrn=1&service_info_poi=803343',
                        [PlatformType.mtAppWebView]: 'imeituan://www.meituan.com/hotel/list?_page_new=1&dsp_init_mrn=1&service_info_poi=803343'
                    }
                };
                const platform = getPlatformType();
                const type = prize.rewardType;
                url = mapper[type]?.[platform] || '';
            }
            if (url) {
                monitorNavigateTo({
                    url,
                    _web: {
                        miniProgramNative: true
                    },
                    _meituan: {
                        enableElinkLog: true // 开启 Elink 日志上报
                    }
                });
            }
            else {
                // 上报没有跳转链接的奖励
                ctx?._raptorReport?.monitorReportLog?.('没有跳转链接的奖励', {
                    prizeBatchId: prize.prizeBatchId
                }, {
                    category: 'crowdCoupon',
                    level: 'error'
                });
            }
            return;
        }
        if (prize.prizeStatus !== PrizeStatus.CAN_DRAW) {
            // 不满足领取
            return;
        }
        const prizeBatchId2TypeMap = {
            [prize.prizeBatchId]: prize.rewardType
        };
        const result = await drawPrize({
            activityId,
            activityName: String(activityId),
            prizeBatchId2TypeMap
        });
        if (!result) {
            ctx._uiInteractionAPI.toast.show({
                content: '网络异常，请稍后重试'
            });
        }
        else if (result?.userPrizeInfos) {
            updatePrizeListWithReturnData(result.userPrizeInfos);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId, ctx]);
    const handleClaimAllPrize = useCallback(async () => {
        const listData = (Array.isArray(prizeList) ? prizeList : []);
        const canDrawPrizeList = listData.filter(prize => prize.prizeStatus === PrizeStatus.CAN_DRAW);
        if (canDrawPrizeList.length === 0) {
            // 上报没有可领取的奖励
            ctx?._raptorReport?.monitorReportLog?.('无可领取奖励', {
                totalCount: listData.length,
                canDrawCount: canDrawPrizeList.length
            }, {
                category: 'crowdCoupon',
                level: 'warn'
            });
            return;
        }
        // 上报一键领取点击埋点
        ctx._LXReport.mcReport(LX_CONFIG.CLAIM_ALL_MC, {
            valLab: {
                coupon_num: listData.length,
                coupon_batch_id: canDrawPrizeList.map(prize => prize.prizeBatchId).join(',')
            }
        });
        const prizeBatchId2TypeMap = {};
        canDrawPrizeList.forEach(prize => {
            prizeBatchId2TypeMap[prize.prizeBatchId] = prize.rewardType;
        });
        const result = await drawPrize({
            activityId,
            activityName: String(activityId),
            prizeBatchId2TypeMap
        });
        if (!result) {
            ctx._uiInteractionAPI.toast.show({
                content: '网络异常，请稍后重试'
            });
        }
        else if (result?.userPrizeInfos) {
            updatePrizeListWithReturnData(result.userPrizeInfos);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId, prizeList, ctx]);
    // 根据布局类型获取对应的卡片组件
    const getCardComponent = useCallback((prize) => {
        if (layoutType === LayoutType.DOUBLE) {
            return <CouponCardDouble prize={prize} onPress={handleDrawPrize}/>;
        }
        if (layoutType === LayoutType.TRIPLE) {
            return <CouponCardTriple prize={prize} onPress={handleDrawPrize}/>;
        }
        return <CouponCardSingle prize={prize} onPress={handleDrawPrize}/>;
    }, [layoutType, handleDrawPrize]);
    // 卡片曝光埋点
    const handlePrizeExposure = useCallback((prize) => {
        ctx._LXReport.mvReport(LX_CONFIG.COUPON_CARD_MV, {
            valLab: {
                coupon_batch_id: prize.prizeBatchId,
                coupon_status: prize.prizeStatus
            }
        });
    }, [ctx]);
    // 一键领取区域曝光埋点
    const handleClaimAllMv = useCallback(() => {
        ctx._LXReport.mvReport(LX_CONFIG.CLAIM_ALL_MV, {
            valLab: {}
        });
    }, [ctx]);
    // @ts-ignore
    if (window?.inEditor) {
        return <Image source={{
                uri: 'https://p0.meituan.net/mcpmaterialpreview/1c2183aefdc95840c3b65edbe7fb462c122715.png'
            }} style={{
                width: '702rpx',
                height: '380rpx',
                display: 'flex',
                margin: '0 auto'
            }} borderRadius={0}/>;
    }
    // 横向滚动布局渲染逻辑（包括 HORIZONTAL 和 PACKAGE）
    if (layoutType === LayoutType.HORIZONTAL) {
        return <View className="crowd-coupon-container">
        <CouponHorizontal prizes={prizeList} backgroundImage={packageBackgroundImg} backgroundWidth={packageBackgroundWidth} backgroundHeight={packageBackgroundHeight} scrollViewMarginTop={packageMarginTop} onPrizePress={handleDrawPrize} onClaimAllPress={handleClaimAllPrize} isShowClaimAll={isShowClaimAll} claimOnClickConfig={claimOnClickConfig} handleMvReport={handlePrizeExposure} handleClaimAllMv={handleClaimAllMv} title={title} titleColor={titleColor}/>
      </View>;
    }
    // 其他布局（单列、双列、三列）渲染逻辑
    return <View className="crowd-coupon-container">
      {content ? <Text className="crowd-coupon-title">{content}</Text> : null}
      <View className={`prize-list ${layoutType === LayoutType.DOUBLE ? 'double-column' : layoutType === LayoutType.TRIPLE ? 'triple-column' : 'single-column'}`}>
        {prizeList.map((prize, index) => <Sensor key={`${prize.prizeBatchId}-${index}`} once onAppear={() => handlePrizeExposure(prize)}>
            {getCardComponent(prize)}
          </Sensor>)}
      </View>
    </View>;
};
export default memo(CrowdCouponComponent);
